import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Socket } from "socket.io-client";
import EcommerceListView from "@components/order_entry/EcommerceListView/EcommerceListView";
import ICatalog_Item from "@models/ICatalog_Item";
import EcommerceCardFieldEnum from "@models/EcommerceCardFieldEnum";
import EcommerceCardViewService, { ICardViewSettings } from "@services/EcommerceCardViewService";
import IOrder from "../../../../models/IOrder";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { EcommerceCardFieldOrderConfig, FIELD_LABEL_KEYS } from "../EcommerceCardFieldOrderConfig/EcommerceCardFieldOrderConfig";
import { useSettingsPage } from "../SettingsPage/SettingsPageContext";
import SettingsRow from "../SettingsRow/SettingsRow";

type Props = {
    /** The working card view settings (`settings.screens.eCommerce.cardView`), saved or unsaved. */
    cardView: ICardViewSettings | undefined;
    /** Without par levels the PAR field is neither shown on the storefront nor offered here. */
    hasParLevels: boolean;
    onChange: (fieldOrder: EcommerceCardFieldEnum[], visibility: Record<EcommerceCardFieldEnum, boolean>) => void;
};

/** A stand-in item so the preview card shows every field the storefront can render. */
const PREVIEW_ITEM = {
    has_inventory: true,
    qty_on_hand: 100,
    msrp: 100.0,
    name: "Test Item Name",
    sku: "TEST-SKU",
    description: "Test Description",
    short_description: "Test Short Description",
    Category: { id: 1, name: "Test Category" },
    pricing: { wholesale: 100.0 },
    customer_par_qty: 12,
} as ICatalog_Item;

const PREVIEW_ORDER = { Price_Group: { value: "wholesale" } } as IOrder;

/**
 * The eCommerce Card View setting as one collapsed row: a summary of which fields show, and an
 * Edit button that opens the live preview card next to the drag-to-reorder field list.
 */
export const EcommerceCardViewSettings = (props: Props) => {
    const intl = useIntl();
    const page = useSettingsPage();
    const [expanded, setExpanded] = useState(false);

    const normalized = useMemo(() => EcommerceCardViewService.normalizeCardView(props.cardView), [props.cardView]);
    const previewCardView = useMemo(() => EcommerceCardViewService.denormalizeCardView(normalized), [normalized]);

    const excludeFields = props.hasParLevels ? [] : [EcommerceCardFieldEnum.PAR];
    const offered = normalized.fieldOrder.filter((field) => !excludeFields.includes(field));
    const shown = offered.filter((field) => normalized.visibility[field]);

    const label = (key: string) => intl.formatMessage({ id: key, defaultMessage: `_${key}_` });

    const summary = intl.formatMessage(
        { id: "fields_shown", defaultMessage: "{shown} of {total} fields shown" },
        { shown: shown.length, total: offered.length }
    );
    const shownLabels = shown
        .map((field) =>
            field === EcommerceCardFieldEnum.ACTIONS ? `${label("quantity")} / ${label("add_to_cart")}` : label(FIELD_LABEL_KEYS[field])
        )
        .join(", ");

    const control = (
        <div className={"SettingsCardView-summary"}>
            <div className={"SettingsCardView-summary-text"}>
                <span className={"SettingsChip"} data-testid={"card-view-shown-count"}>
                    {summary}
                </span>
                <span className={"SettingsCardView-summary-fields"} data-testid={"card-view-shown-fields"}>
                    {shownLabels}
                </span>
            </div>
            <OwButton
                title={expanded ? "done" : "edit"}
                className={expanded ? "btn-outline-secondary" : "btn-primary"}
                leftIconClass={expanded ? "fad fa-check" : "fad fa-pen-to-square"}
                fn={() => setExpanded(!expanded)}
                disableDblClickPrevention={true}
                disableWidthOnClick={true}
                testId={"card-view-edit"}
            />
        </div>
    );

    return (
        <SettingsRow
            title={"ecommerce_card_view"}
            detail={"help_text_ecommerce_card_view"}
            name={"ecommerce_card_view"}
            // Changes land in settings.screens, which is what the page diffs against the saved model.
            editedKey={"settings.screens"}
            component={control}
            isActive={expanded}>
            <div className={"SettingsCardView-editor"} data-testid={"card-view-editor"}>
                <div className={"SettingsCardView-preview"} style={{ top: page.headerHeight + 12 }}>
                    <OwLabel title={"preview"} className={"SettingsCardView-caption"} noBottomMargin={true} />
                    <div className={"SettingsCardView-card"}>
                        <EcommerceListView
                            addToCart={() => {}}
                            openDetailView={() => {}}
                            isReadOnly={false}
                            order={PREVIEW_ORDER}
                            socket={{} as Socket}
                            cardViewOverride={previewCardView}
                            catalogItem={PREVIEW_ITEM}
                            priceGroupName={"wholesale"}
                        />
                    </div>
                </div>
                <div className={"SettingsCardView-fields"}>
                    <OwLabel title={"card_view_items"} className={"SettingsCardView-caption"} noBottomMargin={true} />
                    {/* The par level entry only exists for par-level suppliers (the storefront never renders it otherwise). */}
                    <EcommerceCardFieldOrderConfig
                        fieldOrder={normalized.fieldOrder}
                        visibility={normalized.visibility}
                        onChange={props.onChange}
                        excludeFields={excludeFields}
                    />
                </div>
            </div>
        </SettingsRow>
    );
};

export default EcommerceCardViewSettings;
